/**
 * @file     mortgage.ts
 * @purpose  Shared Canadian mortgage-rate conversion and amortization
 *           primitives used by calculators, affordability, and rent models.
 */

/** The qualifying rate floor: contract rate plus 2%, or 5.25%, whichever is higher. */
export const STRESS_TEST_FLOOR = 0.0525;

/** The buffer OSFI B-20 adds to the contract rate before comparing it to the floor. */
export const STRESS_TEST_BUFFER = 0.02;

/**
 * Illustrative typical five-year fixed CONTRACT rate: what a well-qualified
 * buyer might actually be offered. Used for affordability and qualification
 * (lender ceiling, stress test, pre-approval, the calculators' starting rate).
 * Illustrative only, never a quote; surfaces that show it say so.
 */
export const TYPICAL_CONTRACT_RATE = 0.0449;

/**
 * Illustrative POSTED five-year fixed rate: the bank's published rate, higher
 * than what most buyers sign at. Used for carrying cost (rent-vs-mortgage
 * pages, cost of waiting, the listing card `~$X/mo` line), where overstating
 * the payment is the safer error. Illustrative only, never a quote.
 */
export const POSTED_FIVE_YEAR_RATE = 0.0519;

/** The same two rates as percentages (4.49, 5.19) for inputs and URL params that take percent. */
export const TYPICAL_CONTRACT_RATE_PCT = Math.round(TYPICAL_CONTRACT_RATE * 10_000) / 100;
export const POSTED_FIVE_YEAR_RATE_PCT = Math.round(POSTED_FIVE_YEAR_RATE * 10_000) / 100;

/** Kept so relta-mobile's verbatim copy of this file and older imports keep compiling. */
export const ILLUSTRATIVE_CONTRACT_RATE = TYPICAL_CONTRACT_RATE;

/** Gross debt service: the share of gross income a lender allows for housing. */
export const GDS_RATIO = 0.39;

/** Total debt service: the share allowed for housing plus all other debt. */
export const TDS_RATIO = 0.44;

/** The amortization the illustrative figures assume, in months. */
export const AMORTIZATION_MONTHS = 300;
export const AMORTIZATION_YEARS = AMORTIZATION_MONTHS / 12;

/** The rate a lender underwrites to, which is not the rate you pay. */
export function stressTestRate(contractRate: number): number {
  return Math.max(contractRate + STRESS_TEST_BUFFER, STRESS_TEST_FLOOR);
}

/** `stressTestRate` for callers that hold rates as percentages (4.49 in, 6.49 out). */
export function stressTestRatePct(contractRatePct: number): number {
  return Math.round(stressTestRate(contractRatePct / 100) * 1_000_000) / 10_000;
}

/** Converts a nominal annual rate compounded semi-annually to a periodic rate. */
export function canadianPeriodicRate(
  nominalAnnualRate: number,
  periodsPerYear: number,
): number {
  if (nominalAnnualRate <= 0 || periodsPerYear <= 0) return 0;
  const effectiveAnnual = Math.pow(1 + nominalAnnualRate / 2, 2) - 1;
  return Math.pow(1 + effectiveAnnual, 1 / periodsPerYear) - 1;
}

/**
 * The one payment function: level payment on a Canadian fixed-rate mortgage,
 * nominal annual rate compounded semi-annually (Interest Act s.6). Every
 * calculator, chart and page computes P&I through here.
 */
export function amortizedPayment(
  principal: number,
  nominalAnnualRate: number,
  totalPeriods: number,
  periodsPerYear = 12,
): number {
  if (principal <= 0 || totalPeriods <= 0) return 0;
  const rate = canadianPeriodicRate(nominalAnnualRate, periodsPerYear);
  if (rate === 0) return principal / totalPeriods;
  return (principal * rate) / (1 - Math.pow(1 + rate, -totalPeriods));
}

export function principalForPayment(
  payment: number,
  nominalAnnualRate: number,
  totalPeriods: number,
  periodsPerYear = 12,
): number {
  if (payment <= 0 || totalPeriods <= 0) return 0;
  const rate = canadianPeriodicRate(nominalAnnualRate, periodsPerYear);
  if (rate === 0) return payment * totalPeriods;
  return (payment * (1 - Math.pow(1 + rate, -totalPeriods))) / rate;
}
